"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { AirdropFarmingBotDto, DeltaNeutralBotDto } from "@cede/types";
import { FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import { BotStrategy, GRID_BOT_CONFIG, DN_BOT_CONFIG, type SortKey, type SortDir } from "@/constants/airdrop-farming";
import { ROUTES } from "@/constants/routes";
import { useAirdropFarmingBots } from "@/hooks/airdrop-farming/useAirdropFarmingBots";
import { useDnBots } from "@/hooks/airdrop-farming/useDnBots";
import { useTrackEvent } from "@/hooks/data/useMetricsEvents";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagsReady } from "@/hooks/feature-flags";
import { useFeatureAccess } from "@/hooks/feature-flags/useFeatureAccess";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useAirdropFarmingIntroStore } from "@/store/airdropFarmingIntroStore";
import { sortBots, filterBotsByStatus, countBotsByStatus } from "@/utils/airdrop-farming";
import Decimal from "decimal.js";
import { useRouter } from "next/navigation";

export enum ContentView {
  BOTS = "bots",
  PERFORMANCE = "performance",
  FUNDING_RATES = "funding-rates",
}

export interface Aggregates {
  totalVolume: Decimal;
  totalPnl: Decimal;
  totalFees: Decimal;
  totalOI: Decimal;
  totalBots: number;
}

export function usePerpsAlgoTradeState() {
  const router = useRouter();
  const { authenticated } = useHubUser();
  const isEnabled = useFeatureAccess(FEATURE_FLAGS.AIRDROP_FARMING_HUB_ENABLE);
  const isFundingRatesEnabled = useFeatureAccess(FEATURE_FLAGS.ARBITRAGE_HUB_FUNDING_RATES);
  const featureFlagsReady = useFeatureFlagsReady();
  const { bots, isLoading, isError, refetch } = useAirdropFarmingBots();
  const { dnBots, isLoading: isDnLoading, isError: isDnError, refetch: refetchDn } = useDnBots();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<BotStrategy>(BotStrategy.GRID);
  const [contentView, setContentView] = useState<ContentView>(ContentView.BOTS);
  const [duplicateBot, setDuplicateBot] = useState<AirdropFarmingBotDto | null>(null);
  const [duplicateDnBot, setDuplicateDnBot] = useState<DeltaNeutralBotDto | null>(null);
  const { hasSeenIntro, setHasSeenIntro } = useAirdropFarmingIntroStore();
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const { trackEvent } = useMetricsEvents();
  useTrackEvent({ event: METRICS_EVENTS.AIRDROP_FARMING_INTRO_VIEWED, enabled: !hasSeenIntro });
  useTrackEvent({ event: METRICS_EVENTS.AIRDROP_FARMING_PAGE_VIEWED, enabled: hasSeenIntro });

  useEffect(() => {
    if (featureFlagsReady && !isEnabled) {
      router.push(ROUTES.HOME);
    }
  }, [featureFlagsReady, isEnabled, router]);

  const handleDuplicate = useCallback((bot: AirdropFarmingBotDto) => {
    setDuplicateBot(bot);
    setDuplicateDnBot(null);
    setModalOpen(true);
  }, []);

  const handleDuplicateDn = useCallback((bot: DeltaNeutralBotDto) => {
    setDuplicateDnBot(bot);
    setDuplicateBot(null);
    setModalOpen(true);
  }, []);

  const handleModalOpenChange = useCallback((open: boolean) => {
    setModalOpen(open);
    if (!open) {
      setDuplicateBot(null);
      setDuplicateDnBot(null);
    }
  }, []);

  const aggregates = useMemo<Aggregates>(() => {
    let totalVolume = new Decimal(0);
    let totalPnl = new Decimal(0);
    let totalFees = new Decimal(0);
    let totalOI = new Decimal(0);

    for (const bot of bots) {
      totalVolume = totalVolume.add(bot.totalVolumeUsdc);
      totalPnl = totalPnl.add(bot.realizedPnlUsdc).add(bot.unrealizedPnlUsdc);
      totalFees = totalFees.add(bot.totalFeesUsdc);
    }

    for (const bot of dnBots) {
      totalOI = totalOI.add(bot.totalOpenInterestUsdc);
      totalPnl = totalPnl.add(bot.netPnlUsdc);
    }

    const totalBots = bots.length + dnBots.length;
    return { totalVolume, totalPnl, totalFees, totalOI, totalBots };
  }, [bots, dnBots]);

  // Sort/filter -- shared across tabs
  const handleSortChange = useCallback((key: SortKey, dir: SortDir) => {
    setSortKey(key);
    setSortDir(dir);
  }, []);

  const handleStatusToggle = useCallback((status: string) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const handleStatusClear = useCallback(() => setStatusFilter(new Set()), []);

  useEffect(() => {
    const allowedStatuses = new Set(
      activeTab === BotStrategy.GRID ? GRID_BOT_CONFIG.filterStatuses : DN_BOT_CONFIG.filterStatuses,
    );
    setStatusFilter((prev) => {
      const next = new Set([...prev].filter((status) => allowedStatuses.has(status)));
      return next.size === prev.size ? prev : next;
    });
  }, [activeTab]);

  const botCounts = useMemo(
    () =>
      activeTab === BotStrategy.GRID
        ? countBotsByStatus(bots, GRID_BOT_CONFIG)
        : countBotsByStatus(dnBots, DN_BOT_CONFIG),
    [bots, dnBots, activeTab],
  );

  const filteredGridBots = useMemo(() => filterBotsByStatus(bots, statusFilter, GRID_BOT_CONFIG), [bots, statusFilter]);

  const filteredDnBots = useMemo(() => filterBotsByStatus(dnBots, statusFilter, DN_BOT_CONFIG), [dnBots, statusFilter]);

  const sortedGridBots = useMemo(
    () => sortBots(filteredGridBots, sortKey, sortDir, GRID_BOT_CONFIG),
    [filteredGridBots, sortKey, sortDir],
  );

  const sortedDnBots = useMemo(
    () => sortBots(filteredDnBots, sortKey, sortDir, DN_BOT_CONFIG),
    [filteredDnBots, sortKey, sortDir],
  );

  const isGridTab = activeTab === BotStrategy.GRID;
  const isDnTab = activeTab === BotStrategy.DELTA_NEUTRAL;
  const hasBots = bots.length > 0 || dnBots.length > 0;
  const showCenteredConnectEmptyState = !authenticated && !hasBots;

  return {
    // Feature flag / routing
    featureFlagsReady,
    isEnabled,
    isFundingRatesEnabled,

    // Auth
    authenticated,

    // Bots data
    bots,
    isLoading,
    isError,
    refetch,
    dnBots,
    isDnLoading,
    isDnError,
    refetchDn,
    // Modal state
    modalOpen,
    setModalOpen,
    duplicateBot,
    duplicateDnBot,
    handleModalOpenChange,

    // Intro
    hasSeenIntro,
    setHasSeenIntro,

    // Tabs
    activeTab,
    setActiveTab,
    contentView,
    setContentView,
    isGridTab,
    isDnTab,

    // Aggregates
    aggregates,
    hasBots,
    showCenteredConnectEmptyState,

    // Sort/filter
    sortKey,
    sortDir,
    handleSortChange,
    statusFilter,
    handleStatusToggle,
    handleStatusClear,
    botCounts,
    filteredGridBots,
    filteredDnBots,
    sortedGridBots,
    sortedDnBots,

    // Actions
    handleDuplicate,
    handleDuplicateDn,
    trackEvent,
  };
}
